import { createRouter, createWebHistory } from 'vue-router';

import store from '@/store/index.js';
import EventBus from '@/elements/eventBus.js';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const isPasswordPage = (name) => {
  const passwordPages = ['set-password', 'reset-password'];
  return passwordPages.includes(name);
};

const permissionGuard = (permissions) => (to, from, next) => {
  if (isPasswordPage(from.name)) {
    next();
    return;
  }
  if (store.state.loggedAsContact) {
    next({ name: 'inbox' });
    return;
  }

  if (Array.isArray(permissions)) {
    if (permissions.some((permission) => store.getters['profile/checkOperations'](permission))) {
      next();
    } else {
      next({ name: 'main' });
    }
    return;
  }
  if (store.getters['profile/checkOperations'](permissions)) {
    next();
  } else {
    next({ name: 'main' });
  }
};

const checkUserToUserVisibility = (next) => {
  if (store.getters['account/isUserToUserVisibilityEnabled']) {
    next();
  } else {
    next({ name: 'main' });
  }
};

const userToUserGuard = (to, from, next) => {
  const packagesList = store.state.account.packagesList;
  if (packagesList.length > 0) {
    checkUserToUserVisibility(next);
    return;
  }
  store.dispatch('account/getAccountInfo').then(() => {
    checkUserToUserVisibility(next);
  });
};

const router = createRouter({
  linkActiveClass: 'current-btn',
  history: createWebHistory(),
  routes: [
    {
      path: '/',
      alias: '/:pathMatch(.*)*',
      name: 'main',
      component: {
        template: '<router-view/>',
      },
      redirect: () => {
        const modules = store.getters.getModules;
        if (modules.length > 0) {
          return { name: modules[0].id };
        }
        return { name: 'files' };
      },
    },
    {
      path: '/share',
      name: 'share',
      component: () => import(/* webpackChunkName: "share" */ '@/components/ShareFiles.vue'),
      beforeEnter: permissionGuard('manage_shared_folder'),
    },
    {
      path: '/tracking',
      name: 'tracking',
      component: () => import(/* webpackChunkName: "tracking" */ '@/components/TrackingPage.vue'),
      beforeEnter: permissionGuard('manage_shared_folder'),
    },
    {
      path: '/contacts',
      name: 'contacts',
      component: () => import(/* webpackChunkName: "contacts" */ '@/components/ContactsPage.vue'),
      beforeEnter: permissionGuard('manage_shared_folder'),
    },
    {
      path: '/files/:bc*',
      name: 'files',
      component: () => import(/* webpackChunkName: "files" */ '@/components/Files.vue'),
      beforeEnter: permissionGuard('manage_files'),
    },
    {
      path: '/automations/internal',
      redirect: { name: 'file-operations' },
    },
    {
      path: '/automations',
      redirect: { name: 'file-operations' },
      component: () => import(/* webpackChunkName: "automations" */ '@/components/Automations.vue'),
      beforeEnter: permissionGuard('manage_automation'),
      children: [
        {
          path: 'internal/file-operations/:action?/:id?',
          name: 'file-operations',
          component: () =>
            import(/* webpackChunkName: "fileOperationList" */ '@/components/automation/FileOperationList.vue'),
        },
        {
          path: 'internal/autodeletes/:action?/:id?',
          name: 'autodeletes',
          component: () =>
            import(/* webpackChunkName: "autodeleteList" */ '@/components/automation/autodeleteList.vue'),
        },
        {
          path: 'internal/user-autodelete/:action?/:id?',
          name: 'user-autodelete',
          component: () =>
            import(/* webpackChunkName: "autoDeleteUsersList" */ '@/components/automation/AutoDeleteUsersList.vue'),
          beforeEnter: permissionGuard('manage_user'),
        },
        {
          path: 'internal/contact-autodelete/:action?/:id?',
          name: 'contact-autodelete',
          component: () =>
            import(
              /* webpackChunkName: "autoDeleteContactsList" */ '@/components/automation/AutoDeleteContactsList.vue'
            ),
          beforeEnter: permissionGuard('manage_user'),
        },
        {
          path: 'internal/activity-export/:action?/:id?',
          name: 'activity-export',
          component: () =>
            import(/* webpackChunkName: "activityExportList" */ '@/components/automation/activityExportList.vue'),
        },
        {
          path: 'external/:action?/:id?',
          name: 'external',
          component: () =>
            import(
              /* webpackChunkName: "externalAutomationsList" */ '@/components/automation/ExternalAutomationsList.vue'
            ),
        },
        {
          path: 'remote-site/:action?/:remoteId?',
          name: 'remote-site',
          component: () =>
            import(/* webpackChunkName: "remoteSiteList" */ '@/components/automation/RemoteSiteList.vue'),
        },
        {
          path: 'remote-agent',
          name: 'remote-agent',
          component: () =>
            import(/* webpackChunkName: "remoteAgentList" */ '@/components/automation/RemoteAgentList.vue'),
        },
        {
          path: 'schedule/:action?/:id?',
          name: 'schedule',
          component: () => import(/* webpackChunkName: "scheduleOverview" */ '@/components/ScheduleOverview.vue'),
        },
      ],
    },
    {
      path: '/administration',
      component: () => import(/* webpackChunkName: "admin" */ '@/components/Admin.vue'),
      beforeEnter: permissionGuard(['manage_user', 'manage_account']),
      children: [
        {
          path: 'account-info',
          name: 'account-info',
          component: () => import(/* webpackChunkName: "accInfo" */ '@/components/administration/accInfo.vue'),
        },
        {
          path: 'users',
          name: 'users',
          component: () => import(/* webpackChunkName: "userList" */ '@/components/administration/userList.vue'),
          beforeEnter: permissionGuard('manage_user'),
        },
        {
          path: 'contacts',
          name: 'admin-contacts',
          component: () =>
            import(/* webpackChunkName: "ContactsList" */ '@/components/administration/contacts/ContactsList.vue'),
          beforeEnter: permissionGuard('manage_user'),
        },
        {
          path: 'links',
          name: 'links',
          component: () =>
            import(
              /* webpackChunkName: "UserToUserVisibility" */ '@/components/administration/UserToUserVisibility.vue'
            ),
          beforeEnter: (...args) => userToUserGuard(...args) && permissionGuard('manage_user')(...args),
        },
        {
          path: 'groups',
          name: 'groups',
          component: () => import(/* webpackChunkName: "groupList" */ '@/components/administration/GroupList.vue'),
          beforeEnter: permissionGuard('manage_group'),
        },
        {
          path: 'service-settings',
          name: 'service-settings',
          component: () =>
            import(/* webpackChunkName: "serviceSettings" */ '@/components/administration/serviceSettings.vue'),
          beforeEnter: permissionGuard('manage_account'),
        },
        {
          path: 'quarantine',
          name: 'quarantine',
          component: () =>
            import(/* webpackChunkName: "AccQuarantine" */ '@/components/administration/quarantine/AccQuarantine.vue'),
          beforeEnter: permissionGuard('manage_account'),
        },
        {
          path: 'rejection-list',
          name: 'rejection-list',
          component: () =>
            import(/* webpackChunkName: "RejectionList" */ '@/components/administration/RejectionList.vue'),
          beforeEnter: permissionGuard('manage_account'),
        },
        {
          path: 'sso',
          name: 'sso',
          component: () => import(/* webpackChunkName: "ssoList" */ '@/components/administration/sso/ssoList.vue'),
          beforeEnter: permissionGuard('manage_account'),
        },
        {
          path: 'activity-log',
          name: 'activity-log',
          component: () => import(/* webpackChunkName: "activity-log" */ '@/components/administration/ActivityLog.vue'),
          beforeEnter: permissionGuard('manage_shared_folder'),
        },
      ],
    },
    {
      path: '/billing',
      name: 'billing',
      redirect: { name: 'invoices' },
      component: () => import(/* webpackChunkName: "billing" */ '@/components/Billing.vue'),
      beforeEnter: permissionGuard('manage_billing'),
      children: [
        {
          path: 'invoices',
          name: 'invoices',
          component: () => import(/* webpackChunkName: "invoiceList" */ '@/components/billing/invoiceList.vue'),
        },
        {
          path: 'cards',
          name: 'cards',
          component: () => import(/* webpackChunkName: "cardList" */ '@/components/billing/cardList.vue'),
        },
      ],
    },
    {
      path: '/inbox',
      name: 'inbox',
      beforeEnter(to, from, next) {
        if (store.state.profile.operationsCode !== 0) {
          next({ name: 'main' });
        } else {
          next();
        }
      },
      component: () => import(/* webpackChunkName: "inbox" */ '@/components/Inbox.vue'),
    },
    {
      path: '/profile',
      beforeEnter(to, from, next) {
        if (!store.state.loggedAsContact) {
          next();
        } else {
          next({ name: 'main' });
        }
      },
      component: () => import(/* webpackChunkName: "profile" */ '@/components/Profile.vue'),
      children: [
        {
          path: '',
          name: 'settings',
          component: () => import(/* webpackChunkName: "generalSettings" */ '@/components/profile/generalSettings.vue'),
        },
        {
          path: 'security',
          name: 'security',
          component: () =>
            import(/* webpackChunkName: "securitySettings" */ '@/components/profile/securitySettings.vue'),
        },
        {
          path: 'sftp',
          name: 'sftp',
          component: () => import(/* webpackChunkName: "sftpSettings" */ '@/components/profile/sftpSettings.vue'),
          beforeEnter(to, from, next) {
            if (store.state.profile.services.some((service) => service.service_key === 'SFTP')) {
              next();
            } else {
              next(from);
            }
          },
        },
        {
          path: 'api-keys',
          name: 'api-keys',
          component: () => import(/* webpackChunkName: "apiKeysSettings" */ '@/components/profile/apiKeysSettings.vue'),
        },
      ],
    },
    {
      path: '/download/:id?',
      name: 'download',
      component: () => import(/* webpackChunkName: "download" */ '@/components/DownloadPage.vue'),
      meta: {
        isStandAlone: true,
        rulesKey: 'downloadPage',
      },
    },
    {
      path: '/set_password/:id?',
      name: 'set-password',
      component: () => import(/* webpackChunkName: "set-password" */ '@/components/SetPassword.vue'),
      meta: {
        isStandAlone: true,
        rulesKey: 'passwordPage',
      },
    },
    {
      path: '/reset_password/:id?',
      name: 'reset-password',
      component: () => import(/* webpackChunkName: "reset-password" */ '@/components/ResetPassword.vue'),
      meta: {
        isStandAlone: true,
        rulesKey: 'passwordPage',
      },
    },
    {
      path: '/files-return/:id?',
      name: 'files-return',
      component: () => import(/* webpackChunkName: "return-files" */ '@/components/ReturnPage.vue'),
      meta: {
        isStandAlone: true,
        rulesKey: 'returnPage',
      },
    },
    {
      path: '/key_request/:id?',
      name: 'key-request',
      component: () => import(/* webpackChunkName: "key-requests" */ '@/components/KeyRequest.vue'),
      meta: {
        isStandAlone: true,
        rulesKey: 'keyRequestPage',
      },
    },
    {
      path: '/msft/:id?',
      name: 'msft',
      component: () => import(/* webpackChunkName: "msft" */ '@/components/msft/msftPage.vue'),
      meta: {
        isStandAlone: true,
        rulesKey: 'msft',
      },
    },
  ],
});

router.beforeEach(async (to, from) => {
  // read query params
  if (Object.keys(to.query).length) {
    // set email from query params for predefining on login form
    if (to.query.email) {
      store.commit('auth/setUserEmail', to.query.email);
    }
    if (to.query.error) {
      store.commit('auth/setLoginError', `sso-${to.query.error}`);
    }
    if (to.query.secure3d) {
      store.commit('billing/setSecure3DCode', to.query.secure3d);
      if (to.name === 'invoices') {
        return { name: 'cards' };
      }
    }
    if (to.query.action === 'pt') {
      EventBus.$emit('showWelcome');
    }
    if (to.query.mfa) {
      store.commit('auth/setForcedMfaCode', +to.query.mfa);
    }
  }

  const isTargetStandAlone = Boolean(to.meta.isStandAlone);

  store.commit('setIsStandAlonePage', isTargetStandAlone);

  //We show 404 error when came to standalone pages without valid uid
  if (isTargetStandAlone && !UUID_REGEX.test(to.params.id)) {
    store.commit('setErrorPageKey', 'errorNotFound');
  }

  if (!isTargetStandAlone) {
    store.commit('setRules', 'main');
  } else {
    // Change checkPermission and errorCodesTree relative to page type
    store.commit('setRules', to.meta.rulesKey);
    EventBus.$emit('changePage');
  }

  if (isTargetStandAlone) {
    if (store.state[to.meta.rulesKey]?.isPageAccessAllowed === false) {
      return false;
    }
  } else if (!isPasswordPage(from.name) && !store.state.auth.isLoggedIn) {
    try {
      await store.dispatch('profile/getProfile');
      if (!store.state.auth.isLoggedIn) {
        return false;
      }
    } catch {
      return false;
    }
  }
  EventBus.$emit('beforeChangeRoute');
});

router.afterEach((to, from) => {
  if (to.meta.isStandAlone) {
    // Avoid login page blicks while metadata did'nt respond yet
    if (store.state[to.meta.rulesKey]?.isPageAccessAllowed === null) {
      EventBus.$emit('beforeChangeRoute');
      return;
    }
  }
  if (to.fullPath !== from.fullPath) {
    EventBus.$emit('afterChangeRoute');
  }
});

export default router;
